import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  EmployeeType,
  IKlearlyAdmin,
} from "../../interfaces/klearlyAdmin.interface";
import { getKlearlyAdminsFromServer } from "../admins/adminsApi";
import { IBusinessFullQuery } from "../../interfaces/businessFull.interface";
import { getSalesRepReportFromServer } from "./SalesApi";

export interface MonthBreakdown {
  month: string;
  grossAmount: number;
  netRevenueAfterMarkup: number;
  bonus: number;
  merchantBonusBreakdown?: { [key: string]: number };
  totalActiveMerchants?: number;
  averageBonusPerMerchant?: number;
}
export interface SalesState {
  salesReps: {
    label: String;
    value: string;
    monthsToExp: number;
    email: string;
  }[];
  salesRepsAnalytics: MonthBreakdown[];
  salesRepsReportUrl: string;
  loadingAnalytics: boolean;
}

const initialState: SalesState = {
  salesReps: [],
  salesRepsAnalytics: [],
  salesRepsReportUrl: "",
  loadingAnalytics: false,
};

export const fetchSalesReps = createAsyncThunk(
  "sales/fetchSalesReps",
  async () => {
    const res = await getKlearlyAdminsFromServer({
      employeeType: "sales" as EmployeeType,
    });
    const admins = (res.admins || []) as IKlearlyAdmin[];

    const toInitial = (name: string) => {
      if (!name) return "";
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return "";
      if (parts.length === 1) return parts[0];
      const first = parts[0];
      const last = parts[parts.length - 1];
      return `${first} ${last.charAt(0)}`;
    };

    return admins.map((a) => ({
      label: a.name,
      value: a.salesId || toInitial(a.name),
      monthsToExp: 13,
      email: a.email,
    }));
  }
);

export const getSalesRepsAnalyticsThunk = createAsyncThunk(
  "sales/analytics",
  async (params: {
    salesRepresentative: string;
    formatType?: "XLSX" | "JSON";
  }) => {
    const report = await getSalesRepReportFromServer(params);
    return report;
  }
);

export const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSalesReps.fulfilled, (state, action) => {
      state.salesReps = action.payload;
    });
    builder.addCase(getSalesRepsAnalyticsThunk.fulfilled, (state, action) => {
      state.salesRepsAnalytics = action.payload.report.MOMBreakdown;
      state.salesRepsReportUrl = action.payload.report.url || "";
      state.loadingAnalytics = false;
    });
    builder.addCase(getSalesRepsAnalyticsThunk.pending, (state, action) => {
      state.loadingAnalytics = true;
    });
    builder.addCase(getSalesRepsAnalyticsThunk.rejected, (state, action) => {
      state.loadingAnalytics = false;
      state.salesRepsAnalytics = [];
      state.salesRepsReportUrl = "";
    });
  },
});

export const selectSalesReps = (state: RootState) => state.sales.salesReps;
export const selectSalesRepsAnalytics = (state: RootState) =>
  state.sales.salesRepsAnalytics;
export const selectSalesRepsReportUrl = (state: RootState) =>
  state.sales.salesRepsReportUrl;
export const selectLoadingAnalytics = (state: RootState) =>
  state.sales.loadingAnalytics;

export default salesSlice.reducer;
